import React, { useState, useEffect, useMemo } from 'react';
import AdminPanel from './AdminPanel';
import { db, collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc } from '../firebase';
import { useTeam } from '../contexts/TeamContext';
import { useTeamPath } from '../hooks/useTeamPath';
import { useCanEditTeam } from '../hooks/useIsTeamOwner';
import { useTranslation } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import '../App.css';

const TimeInput24h = ({ value, onChange, required }) => {
  const [raw, setRaw] = useState(value || '');

  const handleChange = (e) => {
    let v = e.target.value.replace(/[^0-9:]/g, '');
    // auto-ubaci : nakon dva broja
    if (v.length === 2 && !v.includes(':') && raw.length === 1) v = v + ':';
    if (v.length > 5) v = v.slice(0, 5);
    setRaw(v);
    if (/^\d{2}:\d{2}$/.test(v)) {
      const [h, m] = v.split(':').map(Number);
      if (h <= 23 && m <= 59) onChange({ target: { value: v } });
    } else if (v === '') {
      onChange({ target: { value: '' } });
    }
  };

  const handleBlur = () => {
    if (!/^\d{2}:\d{2}$/.test(raw)) setRaw(value || '');
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      placeholder="HH:MM"
      value={raw}
      onChange={handleChange}
      onBlur={handleBlur}
      required={required}
      maxLength={5}
      style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.05em' }}
    />
  );
};

/* eslint-disable jsx-a11y/accessible-emoji */

const Schedule = ({ user }) => {
  const { teamId, loading: teamLoading } = useTeam();
  const tp = useTeamPath();
  const isTeamOwner = useCanEditTeam(user);
  const { language } = useTranslation();
  const [matches, setMatches] = useState([]);

  // Aktivni tab: 'matches' (utakmice), 'players' (igrači) ili 'season' (arhiva/reset).
  // Default je 'matches' jer je to najčešća radnja.
  const [activeTab, setActiveTab] = useState('matches');

  // Quick-add forma state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [submittingMatch, setSubmittingMatch] = useState(false);

  // Edit forma state
  const [editingMatchId, setEditingMatchId] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [editLocation, setEditLocation] = useState('');

  const startEdit = (match) => {
    setEditingMatchId(match.id);
    setEditDate(match.date || '');
    setEditTime(match.time || '');
    setEditLocation(match.location || '');
  };

  const cancelEdit = () => setEditingMatchId(null);

  const saveEdit = async (matchId) => {
    if (!teamId || !editDate) return;
    await updateDoc(doc(db, 'teams', teamId, 'matches', matchId), {
      date: editDate,
      time: editTime,
      location: editLocation,
    });
    setEditingMatchId(null);
  };

  useEffect(() => {
    if (!teamId) return;
    const unsubscribe = onSnapshot(collection(db, "teams", teamId, "matches"), (snapshot) => {
      setMatches(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [teamId]);

  const resetAddForm = () => {
    setNewDate('');
    setNewTime('');
    setNewLocation('');
  };

  const handleAddMatch = async (e) => {
    e.preventDefault();
    if (!teamId) return;
    if (!newDate || !newTime || !newLocation.trim()) {
      alert("Popuni datum, vrijeme i lokaciju.");
      return;
    }

    setSubmittingMatch(true);
    try {
      await addDoc(collection(db, "teams", teamId, "matches"), {
        date: newDate,
        time: newTime,
        location: newLocation.trim(),
        players: [],
      });
      resetAddForm();
      setShowAddForm(false);
    } catch (error) {
      console.error("Greška pri dodavanju termina:", error);
      alert("❌ Greška pri dodavanju termina.");
    } finally {
      setSubmittingMatch(false);
    }
  };

  const deleteMatch = async (id) => {
    if (!teamId) return;
    if (!window.confirm("Jeste li sigurni da želite obrisati ovu utakmicu?")) return;

    try {
      await deleteDoc(doc(db, "teams", teamId, "matches", id));
    } catch (error) {
      console.error("Greška pri brisanju termina:", error);
    }
  };

  const sortedMatches = [...matches].sort((a, b) => new Date(b.date) - new Date(a.date));

  // "Čeka unos statistike" — utakmice u prošlosti gdje stats objekt nije popunjen.
  // Heuristika: utakmica je odigrana ako je datum < danas, a stats prazan ili ne postoji.
  const pendingStatsCount = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return matches.filter((m) => {
      if (!m.date) return false;
      const md = new Date(m.date);
      if (Number.isNaN(md.getTime())) return false;
      const isPast = md < today;
      const hasStats = m.stats && Object.keys(m.stats).length > 0;
      return isPast && !hasStats;
    }).length;
  }, [matches]);

  if (teamLoading) {
    return <div className="loading-screen">Učitavanje tima…</div>;
  }

  return (
    <div className="schedule-container">
      <div className="section-header">
        <h2>🛠️ Upravljanje timom</h2>
        <span className="section-badge">Utakmice i igrači</span>
      </div>
      <p style={{ color: 'var(--text-3)', fontSize: '0.92rem', marginTop: -8, marginBottom: 16 }}>
        Ovdje dodaješ nove utakmice i unosiš statistiku nakon odigrane utakmice, te upravljaš igračima tima.
      </p>

      {/* Tab navigacija — 3 logičke cjeline da se ne mora skrolati */}
      <div className="manage-tabs" role="tablist" aria-label="Upravljanje timom">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'matches'}
          className={`manage-tab ${activeTab === 'matches' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('matches')}
        >
          <span className="manage-tab-label">⚽ Utakmice</span>
          <span className="manage-tab-count">{matches.length}</span>
          {pendingStatsCount > 0 && (
            <span className="manage-tab-pill" title="Utakmice koje čekaju unos statistike">
              {pendingStatsCount} čeka
            </span>
          )}
        </button>
        {isTeamOwner && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'players'}
            className={`manage-tab ${activeTab === 'players' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('players')}
          >
            <span className="manage-tab-label">👥 Igrači</span>
          </button>
        )}
        {isTeamOwner && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'season'}
            className={`manage-tab ${activeTab === 'season' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('season')}
          >
            <span className="manage-tab-label">🗓️ Sezona</span>
          </button>
        )}
      </div>

      {activeTab === 'matches' && (
      <div className="manage-section">
        <div className="section-header" style={{ marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>⚽ Utakmice</h3>
          <span className="section-badge">{matches.length} termina</span>
        </div>
        <p style={{ color: 'var(--text-3)', fontSize: '0.88rem', marginBottom: 14 }}>
          Dodaj novu utakmicu (datum, vrijeme, lokacija). Za unos statistike nakon odigrane utakmice klikni <strong>Uredi</strong> pored termina.
        </p>

        {isTeamOwner && (
          <div className="schedule-quick-add">
            {!showAddForm ? (
              <button
                onClick={() => setShowAddForm(true)}
                className="btn btn-primary btn-lg schedule-quick-add-trigger"
              >
                ➕ Dodaj utakmicu
              </button>
            ) : (
              <form onSubmit={handleAddMatch} className="schedule-quick-add-form">
                <div className="schedule-quick-add-header">
                  <h3>Nova utakmica</h3>
                  <button
                    type="button"
                    onClick={() => { setShowAddForm(false); resetAddForm(); }}
                    className="schedule-quick-add-close"
                    aria-label="Zatvori"
                  >
                    ✕
                  </button>
                </div>
                <div className="schedule-quick-add-grid">
                  <label>
                    <span>📅 Datum</span>
                    <input
                      type="date"
                      value={newDate}
                      onChange={(e) => setNewDate(e.target.value)}
                      required
                    />
                  </label>
                  <label>
                    <span>⏰ Vrijeme</span>
                    {language === 'hr' ? (
                      <TimeInput24h
                        value={newTime}
                        onChange={(e) => setNewTime(e.target.value)}
                        required
                      />
                    ) : (
                      <input
                        type="time"
                        value={newTime}
                        onChange={(e) => setNewTime(e.target.value)}
                        required
                      />
                    )}
                  </label>
                  <label className="schedule-quick-add-location">
                    <span>📍 Lokacija</span>
                    <input
                      type="text"
                      placeholder="npr. Dvorana Infobip"
                      value={newLocation}
                      onChange={(e) => setNewLocation(e.target.value)}
                      required
                    />
                  </label>
                </div>
                <div className="schedule-quick-add-actions">
                  <button
                    type="button"
                    onClick={() => { setShowAddForm(false); resetAddForm(); }}
                    className="btn btn-secondary"
                    disabled={submittingMatch}
                  >
                    Odustani
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submittingMatch}
                  >
                    {submittingMatch ? '⏳ Dodajem…' : '💾 Spremi utakmicu'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        <div className="schedule-list">
          {sortedMatches.length > 0 ? (
            sortedMatches.map((match) => (
              <div key={match.id} className="schedule-card">
                <div className="schedule-info">
                  {editingMatchId === match.id ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <input
                          type="date"
                          value={editDate}
                          onChange={e => setEditDate(e.target.value)}
                          className="form-input"
                          style={{ flex: '1 1 140px' }}
                        />
                        {language === 'hr' ? (
                          <TimeInput24h value={editTime} onChange={e => setEditTime(e.target.value)} />
                        ) : (
                          <input type="time" value={editTime} onChange={e => setEditTime(e.target.value)} className="form-input" style={{ flex: '1 1 120px' }} />
                        )}
                        <input
                          type="text"
                          value={editLocation}
                          onChange={e => setEditLocation(e.target.value)}
                          placeholder="Lokacija"
                          className="form-input"
                          style={{ flex: '2 1 160px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => saveEdit(match.id)} className="btn btn-primary btn-sm">Spremi</button>
                        <button onClick={cancelEdit} className="btn btn-secondary btn-sm">Odustani</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p><strong>📅 Datum:</strong> {match.date}</p>
                      <p><strong>⏰ Vrijeme:</strong> {match.time}</p>
                      <p><strong>📍 Lokacija:</strong> {match.location}</p>
                    </>
                  )}
                </div>

                <div className="schedule-actions">
                  {isTeamOwner && (
                    <>
                      {editingMatchId !== match.id && (
                        <button onClick={() => startEdit(match)} className="btn btn-secondary btn-sm">
                          ✏️ Uredi termin
                        </button>
                      )}
                      <Link to={tp(`/match/${match.id}`)} className="btn btn-info btn-sm">
                        ✏️ Uredi statistiku nakon odigrane utakmice
                      </Link>
                      <button onClick={() => deleteMatch(match.id)} className="btn btn-danger btn-sm">
                        🗑️ Obriši
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">📭 Nema dostupnih utakmica.</div>
          )}
        </div>
      </div>
      )}

      {/* Tab: Igrači — samo player management iz AdminPanela */}
      {activeTab === 'players' && isTeamOwner && (
        <div className="manage-section">
          <AdminPanel user={user} view="players" />
        </div>
      )}

      {/* Tab: Sezona — arhiva i reset (opasne radnje, izolirano) */}
      {activeTab === 'season' && isTeamOwner && (
        <div className="manage-section">
          <AdminPanel user={user} view="season" />
        </div>
      )}
    </div>
  );
};

/* eslint-enable jsx-a11y/accessible-emoji */

export default Schedule;
